import React, { useState, useMemo } from "react";
import { Tag } from "antd";
import ReactECharts from "echarts-for-react";

// ─── Design Tokens ───────────────────────────────────────────────────────────

export const T = {
  bg: "#f0f2f5",
  card: "#ffffff",
  cardHover: "#fafbff",
  text: "#0f172a",
  textSecondary: "#64748b",
  textMuted: "#94a3b8",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",

  indigo: "#6366f1",
  indigoDark: "#4f46e5",
  indigoLight: "#eef2ff",
  violet: "#8b5cf6",
  emerald: "#10b981",
  emeraldLight: "#ecfdf5",
  amber: "#f59e0b",
  amberLight: "#fffbeb",
  rose: "#f43f5e",
  roseLight: "#fff1f2",
  sky: "#0ea5e9",
  skyLight: "#f0f9ff",
  slate: "#475569",
  teal: "#14b8a6",

  shadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
  shadowMd: "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05)",
  shadowLg: "0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -4px rgba(0,0,0,0.05)",
  shadowXl: "0 20px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.05)",
  radius: "16px",
  radiusSm: "12px",
  radiusXs: "8px",
};

// ─── CSS Keyframes (injected once) ───────────────────────────────────────────

const styleId = "ops-dashboard-styles";
if (typeof document !== "undefined" && !document.getElementById(styleId)) {
  const styleSheet = document.createElement("style");
  styleSheet.id = styleId;
  styleSheet.textContent = `
    @keyframes ops-fadeIn {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes ops-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes ops-countUp {
      from { opacity: 0; transform: scale(0.8); }
      to { opacity: 1; transform: scale(1); }
    }
    .ops-card {
      background: ${T.card};
      border-radius: ${T.radius};
      border: 1px solid ${T.border};
      box-shadow: ${T.shadow};
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      animation: ops-fadeIn 0.5s ease-out both;
    }
    .ops-card:hover {
      box-shadow: ${T.shadowLg};
      transform: translateY(-2px);
      border-color: ${T.indigo}22;
    }
    .ops-kpi-card {
      background: ${T.card};
      border-radius: ${T.radius};
      border: 1px solid ${T.border};
      box-shadow: ${T.shadow};
      padding: 20px 24px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      animation: ops-fadeIn 0.5s ease-out both;
      position: relative;
      overflow: hidden;
    }
    .ops-kpi-card:hover {
      box-shadow: ${T.shadowLg};
      transform: translateY(-3px);
      border-color: transparent;
    }
    .ops-table .ant-table {
      border-radius: 0 0 ${T.radius} ${T.radius} !important;
    }
    .ops-table .ant-table-thead > tr > th {
      background: #f8fafc !important;
      border-bottom: 1px solid ${T.border} !important;
      font-weight: 600 !important;
      font-size: 11px !important;
      text-transform: uppercase !important;
      letter-spacing: 0.5px !important;
      color: ${T.textSecondary} !important;
      padding: 12px 16px !important;
    }
    .ops-table .ant-table-tbody > tr > td {
      padding: 14px 16px !important;
      border-bottom: 1px solid ${T.borderLight} !important;
      transition: background 0.2s !important;
    }
    .ops-table .ant-table-tbody > tr:hover > td {
      background: ${T.indigoLight} !important;
    }
    .ops-table .ant-table-expanded-row > td {
      background: #f8fafc !important;
      padding: 0 !important;
    }
    .ops-status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      display: inline-block;
      margin-right: 6px;
    }
    .ops-status-dot.online {
      background: ${T.emerald};
      box-shadow: 0 0 0 3px ${T.emerald}30;
      animation: ops-pulse 2s ease-in-out infinite;
    }
    .ops-status-dot.away { background: ${T.amber}; box-shadow: 0 0 0 3px ${T.amber}30; }
    .ops-status-dot.break { background: ${T.sky}; box-shadow: 0 0 0 3px ${T.sky}30; }
    .ops-status-dot.offline { background: ${T.textMuted}; }
    .ops-avatar {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-weight: 700;
      font-size: 13px;
      flex-shrink: 0;
    }
    .ops-refresh-btn {
      background: linear-gradient(135deg, ${T.indigo}, ${T.violet});
      color: white;
      border: none;
      border-radius: 12px;
      padding: 10px 24px;
      font-weight: 600;
      font-size: 13px;
      cursor: pointer;
      transition: all 0.3s;
      box-shadow: 0 4px 12px ${T.indigo}30;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .ops-refresh-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px ${T.indigo}40;
    }
    .ops-refresh-btn:active { transform: translateY(0); }
    .ops-refresh-btn:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }
    .ops-grid {
      display: grid;
      gap: 16px;
    }
    .ops-grid-6 { grid-template-columns: repeat(6, 1fr); }
    .ops-grid-4 { grid-template-columns: repeat(4, 1fr); }
    .ops-grid-3 { grid-template-columns: repeat(3, 1fr); }
    .ops-grid-2 { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 1200px) {
      .ops-grid-6 { grid-template-columns: repeat(3, 1fr); }
      .ops-grid-4 { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 768px) {
      .ops-grid-6, .ops-grid-4, .ops-grid-3 { grid-template-columns: repeat(2, 1fr); }
      .ops-grid-2 { grid-template-columns: 1fr; }
    }
    .ops-section-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 3px 10px;
      border-radius: 20px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }
    .ops-date-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 8px;
      background: ${T.card};
      border: 1px solid ${T.border};
      border-radius: 14px;
      box-shadow: ${T.shadow};
    }
    .ops-date-btn {
      padding: 7px 16px;
      border: none;
      border-radius: 10px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      background: transparent;
      color: ${T.textSecondary};
      white-space: nowrap;
    }
    .ops-date-btn:hover {
      background: ${T.borderLight};
      color: ${T.text};
    }
    .ops-date-btn.active {
      background: linear-gradient(135deg, ${T.indigo}, ${T.violet});
      color: white;
      box-shadow: 0 2px 8px ${T.indigo}30;
    }
    .ops-date-input {
      padding: 7px 12px;
      border: 1px solid ${T.border};
      border-radius: 10px;
      font-size: 12px;
      font-weight: 500;
      color: ${T.text};
      background: ${T.card};
      outline: none;
      cursor: pointer;
      transition: border-color 0.2s;
    }
    .ops-date-input:focus {
      border-color: ${T.indigo};
      box-shadow: 0 0 0 3px ${T.indigo}15;
    }
  `;
  document.head.appendChild(styleSheet);
}

// ─── Utilities ───────────────────────────────────────────────────────────────

export const fmtHrs = (h) => {
  if (!h || h <= 0) return "0h 0m";
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

export const fmtSec = (s) => fmtHrs(s / 3600);

export const statusColor = (s) => {
  const status = (s || "").toLowerCase();
  if (status.includes("online") || status.includes("active") || status.includes("available")) return "online";
  if (status.includes("away") || status.includes("busy")) return "away";
  if (status.includes("break") || status.includes("lunch")) return "break";
  return "offline";
};

export const gradients = [
  "linear-gradient(135deg, #6366f1, #8b5cf6)",
  "linear-gradient(135deg, #0ea5e9, #06b6d4)",
  "linear-gradient(135deg, #10b981, #14b8a6)",
  "linear-gradient(135deg, #f59e0b, #f97316)",
  "linear-gradient(135deg, #f43f5e, #e11d48)",
  "linear-gradient(135deg, #8b5cf6, #a855f7)",
];

export const getGradient = (i) => gradients[i % gradients.length];

// ─── KPI Card ────────────────────────────────────────────────────────────────

export const KpiCard = ({ label, value, sub, color, gradient, icon, delay = 0 }) => (
  <div className="ops-kpi-card" style={{ animationDelay: `${delay}ms` }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: gradient || color, borderRadius: `${T.radius} ${T.radius} 0 0` }} />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: color || T.text, lineHeight: 1, animation: "ops-countUp 0.6s ease-out both", animationDelay: `${delay + 200}ms` }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4, fontWeight: 500 }}>{sub}</div>}
      </div>
      {icon && (
        <div style={{ width: 42, height: 42, borderRadius: 12, background: gradient || `${color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{icon}</div>
      )}
    </div>
  </div>
);

// ─── Section Header with Badge ──────────────────────────────────────────────

export const SectionHeader = ({ title, subtitle, badge, badgeColor, right }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, marginTop: 28 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, letterSpacing: "-0.3px" }}>{title}</div>
          {badge && (
            <span className="ops-section-badge" style={{ background: `${badgeColor || T.indigo}15`, color: badgeColor || T.indigo }}>
              {badge}
            </span>
          )}
        </div>
        {subtitle && <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{subtitle}</div>}
      </div>
    </div>
    {right}
  </div>
);

// ─── Ring Progress ───────────────────────────────────────────────────────────

export const RingProgress = ({ percent, color, size = 44, strokeWidth = 4 }) => {
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (percent / 100) * c;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={T.borderLight} strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }} />
    </svg>
  );
};

// ─── Refresh Button ──────────────────────────────────────────────────────────

export const RefreshButton = ({ loading, onClick }) => (
  <button className="ops-refresh-btn" onClick={onClick} disabled={loading}>
    {loading ? (
      <>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: "spin 1s linear infinite" }}>
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        Loading...
      </>
    ) : (
      <>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 4v6h6M23 20v-6h-6" />
          <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" />
        </svg>
        Refresh
      </>
    )}
  </button>
);

// ─── Date Picker Bar ────────────────────────────────────────────────────────

const toISO = (d) => d.toISOString().slice(0, 10);

const getPresetDate = (key) => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  if (key === "today") return toISO(now);
  if (key === "yesterday") return toISO(new Date(y, m, d - 1));
  return null;
};

export const DatePickerBar = ({ value, onChange }) => {
  const todayISO = useMemo(() => toISO(new Date()), []);
  const yesterdayISO = useMemo(() => getPresetDate("yesterday"), []);

  const activePreset = value === todayISO || !value ? "today" : value === yesterdayISO ? "yesterday" : "custom";

  const handlePreset = (key) => {
    const iso = getPresetDate(key);
    onChange(key === "today" ? null : iso);
  };

  return (
    <div className="ops-date-bar">
      <button
        className={`ops-date-btn ${activePreset === "today" ? "active" : ""}`}
        onClick={() => handlePreset("today")}
      >
        Today
      </button>
      <button
        className={`ops-date-btn ${activePreset === "yesterday" ? "active" : ""}`}
        onClick={() => handlePreset("yesterday")}
      >
        Yesterday
      </button>
      <input
        type="date"
        className="ops-date-input"
        value={value || todayISO}
        max={todayISO}
        onChange={(e) => {
          const val = e.target.value;
          onChange(val === todayISO ? null : val);
        }}
      />
    </div>
  );
};

// ─── Self Dashboard (Employee View) ──────────────────────────────────────────

export const SelfDashboard = ({ emp }) => {
  if (!emp) return <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>No data found</div>;

  // ── Timer Charts ──
  const productiveChart = {
    tooltip: { trigger: "axis", backgroundColor: "#fff", borderColor: T.border, textStyle: { color: T.text, fontSize: 12 } },
    legend: { bottom: 0, textStyle: { fontSize: 11, color: T.textSecondary } },
    grid: { top: 20, right: 20, bottom: 40, left: 50 },
    xAxis: { type: "category", data: ["Today", "This Week", "This Month"], axisLine: { lineStyle: { color: T.border } }, axisLabel: { fontSize: 11, color: T.textSecondary } },
    yAxis: { type: "value", name: "Hours", nameTextStyle: { fontSize: 11, color: T.textMuted }, axisLine: { show: false }, splitLine: { lineStyle: { color: T.borderLight } }, axisLabel: { fontSize: 11, color: T.textSecondary } },
    series: [
      { name: "Productive", type: "bar", stack: "hours", barWidth: 32, itemStyle: { color: T.emerald }, data: [emp.productiveToday, emp.productiveThisWeek, emp.productiveThisMonth] },
      { name: "Non-Productive", type: "bar", stack: "hours", barWidth: 32, itemStyle: { borderRadius: [6, 6, 0, 0], color: T.rose }, data: [emp.nonProductiveToday, emp.nonProductiveThisWeek, emp.nonProductiveThisMonth] },
    ],
  };

  const taskDonut = {
    tooltip: { trigger: "item", formatter: "{b}: {c}h ({d}%)", backgroundColor: "#fff", borderColor: T.border, textStyle: { color: T.text, fontSize: 12 } },
    legend: { bottom: 0, textStyle: { fontSize: 10, color: T.textSecondary }, type: "scroll" },
    series: [{
      type: "pie", radius: ["48%", "72%"], center: ["50%", "42%"],
      label: { show: false }, emphasis: { label: { show: true, fontSize: 13, fontWeight: 600 } },
      itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
      data: (emp.taskBreakdown || []).map((t, i) => ({
        name: t.taskType, value: t.totalHours,
        itemStyle: { color: [T.indigo, T.emerald, T.amber, T.sky, T.rose, T.violet, T.teal, "#f97316"][i % 8] },
      })),
    }],
  };

  // ── Desktop Activity Charts ──
  const appBarChart = {
    tooltip: { trigger: "axis", backgroundColor: "#fff", borderColor: T.border, textStyle: { color: T.text, fontSize: 12 } },
    grid: { top: 8, right: 20, bottom: 0, left: 100, containLabel: false },
    xAxis: { type: "value", axisLine: { show: false }, splitLine: { lineStyle: { color: T.borderLight } }, axisLabel: { fontSize: 10, color: T.textSecondary, formatter: (v) => fmtSec(v) } },
    yAxis: {
      type: "category", inverse: true, axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { fontSize: 11, color: T.textSecondary, width: 90, overflow: "truncate" },
      data: (emp.topApps || []).map((a) => a.name),
    },
    series: [{
      type: "bar", barWidth: 16, itemStyle: { borderRadius: [0, 8, 8, 0] },
      data: (emp.topApps || []).map((a, i) => ({
        value: a.duration,
        itemStyle: { color: [T.indigo, T.sky, T.emerald, T.violet, T.amber, T.teal, T.rose, "#f97316", "#06b6d4", "#a855f7"][i % 10] },
      })),
    }],
  };

  const totalActivitySec = (emp.totalActive || 0) + (emp.totalIdle || 0);
  const activePercent = totalActivitySec ? Math.round((emp.totalActive / totalActivitySec) * 100) : 0;

  // ── Request Chart ──
  const requestDonut = {
    tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)", backgroundColor: "#fff", borderColor: T.border },
    legend: { bottom: 0, textStyle: { fontSize: 11, color: T.textSecondary } },
    series: [{
      type: "pie", radius: ["50%", "75%"], center: ["50%", "42%"],
      label: { show: false },
      itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
      data: [
        { name: "Completed", value: emp.completedRequests || 0, itemStyle: { color: T.emerald } },
        { name: "Pending", value: emp.pendingRequests || 0, itemStyle: { color: T.amber } },
        { name: "On Hold", value: emp.onHoldRequests || 0, itemStyle: { color: T.rose } },
        // Charted as its own slice so the donut still sums to totalRequests.
        { name: "Cancelled", value: emp.cancelledRequests || 0, itemStyle: { color: T.slate } },
      ].filter((d) => d.value > 0),
    }],
  };

  // ── Comparison values ──
  const timerHoursToday = emp.hoursToday || 0;
  const desktopActiveToday = (emp.totalActive || 0) / 3600;
  const desktopTotalToday = totalActivitySec / 3600;

  return (
    <div>
      {/* ═══════════════════ Profile Bar ═══════════════════ */}
      <div className="ops-card" style={{ padding: "24px 28px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div className="ops-avatar" style={{ background: gradients[0], width: 52, height: 52, fontSize: 18, borderRadius: 14 }}>
            {(emp.employeeName || "?")[0]?.toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: T.text }}>{emp.employeeName}</span>
              <span className={`ops-status-dot ${statusColor(emp.currentStatus)}`} />
              <Tag color={statusColor(emp.currentStatus) === "online" ? "success" : statusColor(emp.currentStatus) === "away" ? "warning" : "default"}
                style={{ borderRadius: 20, fontSize: 11, fontWeight: 600, border: "none" }}>
                {emp.currentStatus || "Offline"}
              </Tag>
            </div>
            <div style={{ fontSize: 13, color: T.textSecondary }}>
              {emp.designation}{emp.designation && emp.department ? " · " : ""}{emp.department}
              {emp.empId ? ` · ${emp.empId}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
            {[
              { label: "Manager", value: emp.reportingManager || "—" },
              { label: "Shift", value: emp.shiftInTime && emp.shiftOutTime ? `${emp.shiftInTime} - ${emp.shiftOutTime}` : "—" },
              { label: "Login", value: emp.loginTime ? new Date(emp.loginTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—" },
              { label: "Clients", value: emp.clients?.length || 0 },
            ].map((item, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══════════════════ Timer vs Desktop Comparison ═══════════════════ */}
      <div className="ops-card" style={{ padding: "20px 24px", marginBottom: 20, background: `linear-gradient(135deg, ${T.indigoLight}, ${T.skyLight})`, border: `1px solid ${T.indigo}20` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>Timer vs Desktop Activity — Today</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 20, alignItems: "center" }}>
          {/* Timer side */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.indigo, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Work Timer</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: T.indigo, lineHeight: 1 }}>{fmtHrs(timerHoursToday)}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Logged via manual timer</div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 8 }}>
              <span style={{ fontSize: 11, color: T.emerald, fontWeight: 600 }}>{fmtHrs(emp.productiveToday)} productive</span>
              <span style={{ fontSize: 11, color: T.rose, fontWeight: 600 }}>{fmtHrs(emp.nonProductiveToday)} non-prod</span>
            </div>
          </div>
          {/* Divider */}
          <div style={{ width: 1, height: 70, background: T.border, position: "relative" }}>
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: T.card, padding: "4px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700, color: T.textMuted, border: `1px solid ${T.border}` }}>VS</div>
          </div>
          {/* Desktop side */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.sky, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Desktop Activity</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: T.sky, lineHeight: 1 }}>{fmtHrs(desktopActiveToday)}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Active on desktop apps</div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 8 }}>
              <span style={{ fontSize: 11, color: T.emerald, fontWeight: 600 }}>{fmtSec(emp.activityProductive)} productive</span>
              <span style={{ fontSize: 11, color: T.amber, fontWeight: 600 }}>{fmtSec(emp.activityUnproductive)} unprod</span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════ SECTION 1: WORK TIMERS ═══════════════════ */}
      <SectionHeader
        title="Work Timers"
        badge="WorkHistory"
        badgeColor={T.indigo}
        subtitle="Hours logged via navbar timer (manual start/stop per task)"
      />

      {/* Timer Hours KPIs */}
      <div className="ops-grid ops-grid-4">
        <KpiCard label="Today" value={fmtHrs(emp.hoursToday)} color={T.indigo} gradient={gradients[0]} delay={0} />
        <KpiCard label="This Week" value={fmtHrs(emp.hoursThisWeek)} color={T.sky} gradient="linear-gradient(135deg, #0ea5e9, #06b6d4)" delay={80} />
        <KpiCard label="This Month" value={fmtHrs(emp.hoursThisMonth)} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #14b8a6)" delay={160} />
        <KpiCard label="This Year" value={fmtHrs(emp.hoursThisYear)} color={T.violet} gradient="linear-gradient(135deg, #8b5cf6, #a855f7)" delay={240} />
      </div>

      {/* Productive vs Non-Productive + Task Breakdown */}
      <div className="ops-grid ops-grid-2" style={{ marginTop: 16 }}>
        <div className="ops-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Productive vs Non-Productive</div>
          <ReactECharts option={productiveChart} style={{ height: 260 }} />
        </div>
        <div className="ops-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Task Distribution (This Month)</div>
          {(emp.taskBreakdown || []).length > 0 ? (
            <ReactECharts option={taskDonut} style={{ height: 260 }} />
          ) : (
            <div style={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}>No task data</div>
          )}
        </div>
      </div>

      {/* Task Breakdown Cards */}
      <div className="ops-card" style={{ padding: 20, marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Task Breakdown Details</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
          {(emp.taskBreakdown || []).map((task, i) => (
            <div key={task.taskType} style={{ padding: "12px 16px", borderRadius: T.radiusXs, border: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: i === 0 ? T.indigoLight : "transparent" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.text, textTransform: "capitalize" }}>{task.taskType}</div>
                <div style={{ fontSize: 10, color: T.textMuted }}>{task.sessionCount} sessions</div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.indigo }}>{fmtHrs(task.totalHours)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Source Breakdown (manual vs auto-detected timer entries) */}
      <div className="ops-card" style={{ padding: 20, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Timer Source Breakdown</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <RingProgress percent={emp.autoDetectionRate || 0} color={T.indigo} size={28} strokeWidth={3} />
            <span style={{ fontSize: 11, fontWeight: 700, color: T.indigo }}>{emp.autoDetectionRate || 0}% auto-detected</span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(emp.sourceBreakdown || []).map((src) => {
            const srcColors = { "auto-detected": T.indigo, "manual-inline": T.emerald, manual: T.amber };
            const srcLabel = src.taskType === "auto-detected" ? "Auto-Detected" : src.taskType === "manual-inline" ? "Manual Inline" : "Manual";
            const srcDesc = src.taskType === "auto-detected" ? "Timer entries created by desktop app" : src.taskType === "manual-inline" ? "Inline edits during active work" : "User clicked Start/Stop";
            const totalSrc = (emp.sourceBreakdown || []).reduce((s, x) => s + x.totalSeconds, 0) || 1;
            const pct = Math.round((src.totalSeconds / totalSrc) * 100);
            return (
              <div key={src.taskType} style={{ padding: "10px 14px", borderRadius: T.radiusXs, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: srcColors[src.taskType] || T.slate, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{srcLabel}</div>
                  <div style={{ fontSize: 10, color: T.textMuted }}>{srcDesc}</div>
                  <div style={{ height: 4, borderRadius: 2, background: T.borderLight, marginTop: 4 }}>
                    <div style={{ height: "100%", borderRadius: 2, background: srcColors[src.taskType] || T.slate, width: `${pct}%`, transition: "width 1s ease" }} />
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: srcColors[src.taskType] || T.slate }}>{fmtHrs(src.totalHours)}</div>
                <Tag style={{ borderRadius: 20, fontSize: 10, fontWeight: 700, border: "none", background: `${srcColors[src.taskType] || T.slate}15`, color: srcColors[src.taskType] || T.slate }}>{pct}%</Tag>
              </div>
            );
          })}
        </div>
      </div>

      {/* Client Work Distribution */}
      <div className="ops-card" style={{ padding: 20, marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Client Work Distribution (This Month)</div>
        {(emp.clientWorkDistribution || []).length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {emp.clientWorkDistribution.map((cw, i) => {
              const maxHrs = Math.max(...emp.clientWorkDistribution.map((c) => c.hours), 1);
              const pct = Math.round((cw.hours / maxHrs) * 100);
              return (
                <div key={cw.clientName} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="ops-avatar" style={{ background: getGradient(i), width: 28, height: 28, fontSize: 10, borderRadius: 8 }}>{cw.clientName[0]?.toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{cw.clientName}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.indigo }}>{fmtHrs(cw.hours)}</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 3, background: T.borderLight }}>
                      <div style={{ height: "100%", borderRadius: 3, background: getGradient(i), width: `${pct}%`, transition: "width 1s ease" }} />
                    </div>
                  </div>
                  <Tag style={{ borderRadius: 20, fontSize: 10, fontWeight: 600, border: "none", background: T.indigoLight, color: T.indigo }}>{cw.requestCount} req</Tag>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}>No client data</div>
        )}
      </div>

      {/* ═══════════════════ SECTION 2: DESKTOP ACTIVITY ═══════════════════ */}
      <SectionHeader
        title="Desktop Activity"
        badge="ActivitySummary"
        badgeColor={T.sky}
        subtitle="Auto-captured from desktop app — apps used, idle time, focus tracking"
      />

      {/* Desktop Activity KPIs */}
      <div className="ops-grid ops-grid-6" style={{ marginBottom: 16 }}>
        {[
          { label: "Active Time", value: fmtSec(emp.totalActive), color: T.emerald },
          { label: "Idle Time", value: fmtSec(emp.totalIdle), color: T.rose },
          { label: "Productive", value: fmtSec(emp.activityProductive), color: T.indigo },
          { label: "Unproductive", value: fmtSec(emp.activityUnproductive), color: T.amber },
          { label: "Focus Switches", value: emp.focusSwitches || 0, color: T.violet },
          { label: "Breaks", value: emp.breakCount || 0, color: T.sky },
        ].map((item, i) => (
          <div key={i} className="ops-kpi-card" style={{ padding: "14px 16px", animationDelay: `${i * 60}ms` }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: item.color }} />
            <div style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>{item.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: item.color }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Top Apps + Browser Domains */}
      <div className="ops-grid ops-grid-2">
        <div className="ops-card" style={{ padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Top Applications</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <RingProgress percent={activePercent} color={T.emerald} size={32} strokeWidth={3} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.emerald }}>{activePercent}% Active</span>
            </div>
          </div>
          {(emp.topApps || []).length > 0 ? (
            <ReactECharts option={appBarChart} style={{ height: Math.max(200, (emp.topApps || []).length * 28) }} />
          ) : (
            <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}>No app data</div>
          )}
        </div>
        <div className="ops-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Top Browser Domains</div>
          {(emp.topBrowserDomains || []).length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {emp.topBrowserDomains.map((bd, i) => {
                const maxD = Math.max(...emp.topBrowserDomains.map((b) => b.duration), 1);
                const pct = Math.round((bd.duration / maxD) * 100);
                return (
                  <div key={bd.domain} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 24, height: 24, borderRadius: 6, background: `${getGradient(i)}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", fontWeight: 700 }}>
                      {(bd.domain || "?")[0]?.toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{bd.domain}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: T.sky }}>{fmtSec(bd.duration)}</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 2, background: T.borderLight }}>
                        <div style={{ height: "100%", borderRadius: 2, background: getGradient(i), width: `${pct}%`, transition: "width 1s ease" }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}>No browser data</div>
          )}
        </div>
      </div>

      {/* ═══════════════════ SECTION 3: REQUESTS ═══════════════════ */}
      <SectionHeader title="Requests & Work Items" badge="Emails" badgeColor={T.emerald} subtitle="Request assignments and completion stats" />
      <div className="ops-grid ops-grid-2">
        <div className="ops-grid ops-grid-2">
          <KpiCard label="Total Requests" value={emp.totalRequests || 0} color={T.indigo} gradient={gradients[0]} />
          <KpiCard label="Completed" value={emp.completedRequests || 0} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #14b8a6)" />
          <KpiCard label="Pending" value={emp.pendingRequests || 0} color={T.amber} gradient="linear-gradient(135deg, #f59e0b, #f97316)" />
          <KpiCard label="On Hold" value={emp.onHoldRequests || 0} color={T.rose} gradient="linear-gradient(135deg, #f43f5e, #e11d48)" />
          <KpiCard label="Cancelled" value={emp.cancelledRequests || 0} color={T.slate} gradient="linear-gradient(135deg, #64748b, #475569)" />
        </div>
        <div className="ops-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>Request Distribution</div>
          {(emp.totalRequests || 0) > 0 ? (
            <ReactECharts option={requestDonut} style={{ height: 240 }} />
          ) : (
            <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}>No requests</div>
          )}
        </div>
      </div>

      {/* ═══════════════════ SECTION 4: LEAVE BALANCE ═══════════════════ */}
      <SectionHeader title="Leave Balance" subtitle={`Year ${new Date().getFullYear()}`} />
      <div className="ops-card" style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 40, justifyContent: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <RingProgress percent={emp.totalLeaves ? Math.round(((emp.totalLeaves - (emp.usedLeaves || 0)) / emp.totalLeaves) * 100) : 100} color={T.emerald} size={120} strokeWidth={10} />
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: T.emerald }}>{emp.availableLeaves || 0}</div>
              <div style={{ fontSize: 9, color: T.textMuted, fontWeight: 600 }}>AVAILABLE</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, minWidth: 300 }}>
            {[
              { label: "Total", value: emp.totalLeaves || 0, color: T.indigo },
              { label: "Used", value: emp.usedLeaves || 0, color: T.rose },
              { label: "Available", value: emp.availableLeaves || 0, color: T.emerald },
            ].map((item) => (
              <div key={item.label} style={{ textAlign: "center", padding: "10px", borderRadius: T.radiusXs, background: `${item.color}08`, border: `1px solid ${item.color}15` }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: item.color }}>{item.value}</div>
                <div style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: "uppercase" }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
